import Button from 'devextreme-react/button';
import LoadIndicator from 'devextreme-react/load-indicator';
import { useCallback, useEffect, useState } from 'react';
import {
  getAnalyticsReadiness,
  getOpenMetadataAdmin,
  retryDatasetMetadataSync,
  type AnalyticsReadiness,
  type MetadataAdminResponse
} from '../../../api/openMetadataAdmin';
import { UtilityAppLayout } from '../../../apps/UtilityAppLayout';
import { showErrorToast, showSuccessToast } from '@jarvis/platform-ui';
import './openMetadataAdmin.css';

const SYNC_LABELS: Record<number, string> = {
  1: 'Pending',
  2: 'Synced',
  3: 'Failed'
};

export function OpenMetadataAdminPage() {
  const [readiness, setReadiness] = useState<AnalyticsReadiness | null>(null);
  const [admin, setAdmin] = useState<MetadataAdminResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ready, overview] = await Promise.all([getAnalyticsReadiness(), getOpenMetadataAdmin()]);
      setReadiness(ready);
      setAdmin(overview);
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : 'Unable to load OpenMetadata status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const retry = async (datasetId: string) => {
    setRetryingId(datasetId);
    try {
      await retryDatasetMetadataSync(datasetId);
      showSuccessToast('Metadata sync queued.');
      await reload();
    } catch (ex) {
      showErrorToast(ex instanceof Error ? ex.message : 'Retry failed.');
    } finally {
      setRetryingId(null);
    }
  };

  const omCheck = readiness?.checks.openmetadata;
  const health = admin?.openMetadataStatus ?? omCheck?.status ?? 'unknown';

  return (
    <UtilityAppLayout
      title="OpenMetadata"
      jobToBeDone="Catalog connectivity and dataset sync status for system administrators."
      variant="wide"
    >
      {loading ? (
        <div className="openmetadata-admin__loading">
          <LoadIndicator />
        </div>
      ) : error ? (
        <p className="openmetadata-admin__error">{error}</p>
      ) : (
        <>
          <section className="openmetadata-admin__card" aria-label="OpenMetadata health">
            <h2>Connectivity</h2>
            <p>
              <strong className={`openmetadata-admin__status openmetadata-admin__status--${health}`}>
                {health}
              </strong>
              <span>
                {' '}
                {admin?.openMetadataDescription ?? omCheck?.description ?? 'Status unavailable.'}
              </span>
            </p>
            <p>
              Bot token configured: <strong>{admin?.botTokenConfigured ? 'yes' : 'no'}</strong>
            </p>
            {admin?.accessGuidance ? (
              <div className="openmetadata-admin__guidance">
                <h3>Browser access</h3>
                <p>
                  OpenMetadata is not opened from this page. The catalog service is loopback-only
                  on the analytics host.
                </p>
                <pre>{admin.accessGuidance}</pre>
              </div>
            ) : null}
            {admin?.openMetadataUrl ? (
              <p>
                <a href={admin.openMetadataUrl} target="_blank" rel="noreferrer">
                  Open OpenMetadata
                </a>
              </p>
            ) : null}
            <p className="openmetadata-admin__muted">
              Pending {admin?.counts.pending ?? 0} · Synced {admin?.counts.synced ?? 0} · Failed{' '}
              {admin?.counts.failed ?? 0}
            </p>
            <Button text="Refresh" stylingMode="outlined" onClick={() => void reload()} />
          </section>

          <section className="openmetadata-admin__card" aria-label="Datasets needing attention">
            <h2>Attention</h2>
            {admin?.attention.length ? (
              <table className="openmetadata-admin__table">
                <thead>
                  <tr>
                    <th>Dataset</th>
                    <th>State</th>
                    <th>Last synced</th>
                    <th>Failure</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {admin.attention.map((row) => (
                    <tr key={row.datasetId}>
                      <td>
                        <a href={`/apps/analytics/workspace/${row.datasetId}`}>{row.name}</a>
                      </td>
                      <td>{SYNC_LABELS[row.syncState] ?? String(row.syncState)}</td>
                      <td>
                        {row.lastSyncedUtc ? new Date(row.lastSyncedUtc).toLocaleString() : '—'}
                      </td>
                      <td>{row.syncFailureReason ?? '—'}</td>
                      <td>
                        <Button
                          text="Retry sync"
                          stylingMode="outlined"
                          disabled={retryingId === row.datasetId}
                          onClick={() => void retry(row.datasetId)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="openmetadata-admin__muted">No pending or failed catalog syncs.</p>
            )}
          </section>
        </>
      )}
    </UtilityAppLayout>
  );
}
